import { Box, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchProductsAsync, resetProductFetchStatus, selectProductFetchStatus, selectProductIsFilterOpen, selectProductTotalResults, selectProducts, toggleFilters } from '../ProductSlice'
import { ProductCard } from './ProductCard'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import AddIcon from '@mui/icons-material/Add'
import { selectBrands } from '../../brands/BrandSlice'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { selectCategories } from '../../categories/CategoriesSlice'
import Pagination from '@mui/material/Pagination'
import { ITEMS_PER_PAGE } from '../../../constants'
import { selectLoggedInUser } from '../../auth/AuthSlice'
import { toast } from 'react-toastify'
import { banner1, banner2, banner3, banner4,banner5,banner6,banner7,banner8,banner9,banner10,  loadingAnimation } from '../../../assets'
import { resetCartItemAddStatus, selectCartItemAddStatus } from '../../cart/CartSlice'
import { motion } from 'framer-motion'
import { ProductBanner } from './ProductBanner'
import ClearIcon from '@mui/icons-material/Clear'
import Lottie from 'lottie-react'
import { Link } from 'react-router-dom'

const sortOptions = [
    { name: 'Price: low to high', sort: 'price', order: 'asc' },
    { name: 'Price: high to low', sort: 'price', order: 'desc' },
]

const bannerImages = [banner1, banner2, banner4, banner5, banner8, banner3, banner6, banner7, banner9, banner10]

export const ProductList = () => {
    const [filters, setFilters] = useState({})
    const [page, setPage] = useState(1)
    const [sort, setSort] = useState(null)
    const theme = useTheme()

    const is1200 = useMediaQuery(theme.breakpoints.down(1200))
    const is800 = useMediaQuery(theme.breakpoints.down(800))
    const is700 = useMediaQuery(theme.breakpoints.down(700))
    const is600 = useMediaQuery(theme.breakpoints.down(600))
    const is500 = useMediaQuery(theme.breakpoints.down(500))
    const is488 = useMediaQuery(theme.breakpoints.down(488))

    const brands = useSelector(selectBrands)
    const categories = useSelector(selectCategories)
    const products = useSelector(selectProducts) || []
    const totalResults = useSelector(selectProductTotalResults) || 0
    const loggedInUser = useSelector(selectLoggedInUser)

    const productFetchStatus = useSelector(selectProductFetchStatus)

    const cartItemAddStatus = useSelector(selectCartItemAddStatus)

    const isProductFilterOpen = useSelector(selectProductIsFilterOpen)

    const dispatch = useDispatch()

    const handleBrandFilters = (e) => {
        const currentBrands = Array.isArray(filters.brand) ? filters.brand : []
        const filterSet = new Set(currentBrands)

        if (e.target.checked) {
            filterSet.add(e.target.value)
        } else {
            filterSet.delete(e.target.value)
        }

        setFilters({ ...filters, brand: Array.from(filterSet) })
    }

    const handleCategoryFilters = (e) => {
        const currentCategories = Array.isArray(filters.category) ? filters.category : []
        const filterSet = new Set(currentCategories)

        if (e.target.checked) {
            filterSet.add(e.target.value)
        } else {
            filterSet.delete(e.target.value)
        }

        setFilters({ ...filters, category: Array.from(filterSet) })
    }

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
    }, [])

    useEffect(() => {
        setPage(1)
    }, [totalResults])

    useEffect(() => {
        const finalFilters = { ...filters }
        finalFilters.pagination = { page, limit: ITEMS_PER_PAGE }
        finalFilters.sort = sort

        if (!loggedInUser?.isAdmin) {
            finalFilters.user = true
        }

        dispatch(fetchProductsAsync(finalFilters))
    }, [dispatch, filters, loggedInUser?.isAdmin, page, sort])

    useEffect(() => {
        if (cartItemAddStatus === 'fulfilled') {
            toast.success('Product added to cart')
        } else if (cartItemAddStatus === 'rejected') {
            toast.error('Error adding product to cart, please try again later')
        }
    }, [dispatch, cartItemAddStatus])

    useEffect(() => {
        if (productFetchStatus === 'rejected') {
            toast.error('Error fetching products, please try again later')
        }
    }, [dispatch, productFetchStatus])

    useEffect(() => {
        return () => {
            dispatch(resetProductFetchStatus())
            dispatch(resetCartItemAddStatus())
        }
    }, [dispatch])

    const handleFilterClose = () => {
        dispatch(toggleFilters())
    }

    return (
        <>
            <motion.div
                style={{ position: 'fixed', backgroundColor: 'white', height: '100vh', padding: '1rem', overflowY: 'scroll', width: is500 ? '100vw' : '30rem', zIndex: 500 }}
                variants={{ show: { left: 0 }, hide: { left: -500 } }}
                initial='hide'
                transition={{ ease: 'easeInOut', duration: 0.7, type: 'spring' }}
                animate={isProductFilterOpen === true ? 'show' : 'hide'}
            >
                <Stack mb={'5rem'} sx={{ scrollBehavior: 'smooth', overflowY: 'scroll' }}>
                    <Typography variant='h4' fontWeight={800}>Discover Your Next Signature Scent</Typography>

                    <IconButton onClick={handleFilterClose} style={{ position: 'absolute', top: 15, right: 15 }}>
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <ClearIcon fontSize='medium' />
                        </motion.div>
                    </IconButton>

                    <Stack rowGap={2} mt={4}>
                        {categories && categories.length > 0 ? (
                            categories.map((cat) => (
                                <Typography
                                    key={cat._id}
                                    sx={{ cursor: 'pointer' }}
                                    variant='body2'
                                    onClick={() => {
                                        setFilters({ ...filters, category: [cat._id] })
                                        setPage(1)
                                        handleFilterClose()
                                    }}
                                >
                                    {cat.name}
                                </Typography>
                            ))
                        ) : (
                            <Typography variant='body2' color='text.secondary'>No categories found</Typography>
                        )}
                    </Stack>

                    <Stack mt={2}>
                        <Accordion>
                            <AccordionSummary
                                expandIcon={
                                    loggedInUser?.isAdmin ? (
                                        <IconButton component={Link} to='/admin/add-brand' size='small' onClick={(e) => e.stopPropagation()}>
                                            <AddIcon />
                                        </IconButton>
                                    ) : (
                                        <AddIcon />
                                    )
                                }
                                aria-controls='brand-filters'
                                id='brand-filters'
                            >
                                <Typography>Brands</Typography>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0 }}>
                                <FormGroup onChange={handleBrandFilters}>
                                    {brands?.map((brand) => (
                                        <motion.div key={brand._id} style={{ width: 'fit-content' }} whileHover={{ x: 5 }} whileTap={{ scale: 0.9 }}>
                                            <FormControlLabel sx={{ ml: 1 }} control={<Checkbox />} label={brand.name} value={brand._id} />
                                        </motion.div>
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>
                    </Stack>

                    <Stack mt={2}>
                        <Accordion>
                            <AccordionSummary
                                expandIcon={
                                    loggedInUser?.isAdmin ? (
                                        <IconButton component={Link} to='/admin/add-category' size='small' onClick={(e) => e.stopPropagation()}>
                                            <AddIcon />
                                        </IconButton>
                                    ) : (
                                        <AddIcon />
                                    )
                                }
                                aria-controls='category-filters'
                                id='category-filters'
                            >
                                <Typography>Category</Typography>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0 }}>
                                <FormGroup onChange={handleCategoryFilters}>
                                    {categories?.map((category) => (
                                        <motion.div key={category._id} style={{ width: 'fit-content' }} whileHover={{ x: 5 }} whileTap={{ scale: 0.9 }}>
                                            <FormControlLabel sx={{ ml: 1 }} control={<Checkbox />} label={category.name} value={category._id} />
                                        </motion.div>
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>
                    </Stack>
                </Stack>
            </motion.div>

            <Stack mb={'3rem'}>
                <Stack
                    sx={{
                        width: '100%',
                        minHeight: { xs: '230px', sm: '320px', md: is800 ? '360px' : is1200 ? '460px' : '560px' },
                        mt: { xs: 1, md: 0 },
                    }}
                >
                    <ProductBanner images={bannerImages} />
                </Stack>

                <Stack rowGap={5} mt={is600 ? 2 : 0} px={{ xs: 1, md: 3 }}>
                    <Stack textAlign="center" rowGap={1} mt={is600 ? 2 : 4}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: { xs: '1.65rem', sm: '2rem', md: '2.35rem' },
                                fontWeight: 900,
                                color: 'primary.main',
                                lineHeight: 1.18,
                            }}
                        >
                            Premium Perfumes, <Box component="span" sx={{ color: 'secondary.dark' }}>Honest Prices</Box>
                        </Typography>
                        <Typography color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                            Explore long-lasting fragrances, testers, and customer-loved scents from Nexocart Perfumes.
                        </Typography>
                    </Stack>
                    <Stack flexDirection={'row'} mr={'2rem'} justifyContent={'flex-end'} alignItems={'center'} columnGap={5}>
                        <Stack alignSelf={'flex-end'} width={'12rem'}>
                            <FormControl fullWidth>
                                <InputLabel id='sort-dropdown'>Sort</InputLabel>
                                <Select variant='standard' labelId='sort-dropdown' label='Sort' onChange={(e) => setSort(e.target.value)} value={sort}>
                                    <MenuItem bgcolor='text.secondary' value={null}>Reset</MenuItem>
                                    {sortOptions.map((option) => (
                                        <MenuItem key={option.name} value={option}>{option.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Stack>

                    {productFetchStatus === 'pending' ? (
                        <Stack width={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'} sx={{ py: 6 }}>
                            <Lottie animationData={loadingAnimation} style={{ width: 120, height: 120 }} />
                        </Stack>
                    ) : (
                        <>
                            <Grid gap={is700 ? 1 : 2} container justifyContent={'center'} alignContent={'center'}>
                                {products.map((product) => (
                                    <ProductCard
                                        key={product._id}
                                        id={product._id}
                                        title={product.title}
                                        thumbnail={product.thumbnail}
                                        brand={typeof product.brand === 'object' ? product.brand?.name : product.brand}
                                        price={product.price}
                                    />
                                ))}
                            </Grid>

                            <Stack alignSelf={is488 ? 'center' : 'flex-end'} mr={is488 ? 0 : 5} rowGap={2} p={is488 ? 1 : 0}>
                                <Pagination size={is488 ? 'medium' : 'large'} page={page} onChange={(e, nextPage) => setPage(nextPage)} count={Math.max(1, Math.ceil(totalResults / ITEMS_PER_PAGE))} variant='outlined' shape='rounded' />
                                <Typography textAlign={'center'}>
                                    Showing {(page - 1) * ITEMS_PER_PAGE + 1} to {Math.min(page * ITEMS_PER_PAGE, totalResults)} of {totalResults} results
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Stack>
        </>
    )
}
